import { Outlet } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  LogOut,
  Menu,
  X,
  Shield,
  Calendar,
  DollarSign,
  Wallet,
  CreditCard,
  Stethoscope,
  UserCircle,
  Flag,
  Activity,
  Clock,
  Settings,
  Megaphone,
  LifeBuoy,
} from 'lucide-react';
import { cn } from '@medikei/ui';
import { useState } from 'react';

interface AdminSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const adminNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Users, label: 'Usuários', path: '/admin/users' },
  { icon: Stethoscope, label: 'Médicos', path: '/admin/medicos' },
  { icon: UserCircle, label: 'Pacientes', path: '/admin/pacientes' },
  { icon: Calendar, label: 'Consultas', path: '/admin/consultas' },
  { icon: Clock, label: 'Filas Expressas', path: '/admin/filas' },
  { icon: DollarSign, label: 'Financeiro', path: '/admin/financeiro' },
  { icon: Wallet, label: 'Carteira', path: '/admin/wallet' },
  { icon: CreditCard, label: 'Saques', path: '/admin/saques' },
  { icon: Users, label: 'Parceiros', path: '/admin/partners' },
  { icon: Flag, label: 'Feature Flags', path: '/admin/feature-flags' },
  { icon: Megaphone, label: 'Anúncios & Banners', path: '/admin/anuncios' },
  { icon: LifeBuoy, label: 'Chamados & Suporte', path: '/admin/chamados' },
  { icon: Stethoscope, label: 'Especialidades', path: '/admin/especialidades' },
  { icon: Activity, label: 'Saúde da API', path: '/admin/api-health' },
  { icon: Settings, label: 'Configurações', path: '/admin/configuracoes' },
];

function AdminSidebar({ collapsed, onToggle }: AdminSidebarProps) {
  return (
    <aside
      className={cn(
        'bg-card border-r border-border transition-all duration-300 flex flex-col',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="h-16 flex items-center justify-between px-4 border-b border-border">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg text-primary">Admin</span>
          </div>
        )}
        <button
          onClick={onToggle}
          className="p-2 rounded-lg hover:bg-accent transition-colors"
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          {collapsed ? <Menu size={20} /> : <X size={20} />}
        </button>
      </div>

      <nav className="flex-1 p-3 space-y-1">
        {adminNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/admin'}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                'hover:bg-accent',
                isActive && 'bg-primary/10 text-primary'
              )
            }
          >
            <item.icon size={20} aria-hidden="true" />
            {!collapsed && (
              <span className="text-sm font-medium">{item.label}</span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-border">
        <button
          onClick={() => {
            localStorage.removeItem('accessToken');
            window.location.href = '/login';
          }}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-destructive hover:bg-destructive/10 transition-colors w-full"
        >
          <LogOut size={20} aria-hidden="true" />
          {!collapsed && (
            <span className="text-sm font-medium">Sair</span>
          )}
        </button>
      </div>
    </aside>
  );
}

export function AdminLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="flex h-screen bg-background">
      <div className="hidden lg:block">
        <AdminSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      </div>

      <div
        className={`fixed inset-0 z-50 bg-black/50 lg:hidden transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      <div
        className={`fixed inset-y-0 left-0 z-50 lg:hidden transition-transform duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <AdminSidebar
          collapsed={false}
          onToggle={() => setMobileMenuOpen(false)}
        />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-accent transition-colors lg:hidden"
            >
              <Menu size={20} />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield size={16} />
              <span>Painel Administrativo</span>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
